import api, { ApiError } from './api';

const DEDUCTION_KEYS = [
  'fuel',
  'advances',
  'insurance',
  'taxes',
  'toll',
  'prepass',
  'eld',
  'damages',
  'other'
];

const BONUS_KEYS = [
  'safety',
  'onTime',
  'efficiency',
  'performance',
  'referral',
  'other'
];

const toNumber = (value, fallback = 0) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const toInteger = (value, fallback = 0) => {
  const numeric = parseInt(value, 10);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const toDateOnly = (value) => {
  if (!value) {
    return '';
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return '';
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return trimmed;
    }

    const parsed = new Date(trimmed);
    return Number.isNaN(parsed.getTime())
      ? ''
      : parsed.toISOString().slice(0, 10);
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? '' : value.toISOString().slice(0, 10);
  }

  if (typeof value.toDate === 'function') {
    const parsed = value.toDate();
    return parsed instanceof Date && !Number.isNaN(parsed.getTime())
      ? parsed.toISOString().slice(0, 10)
      : '';
  }

  const seconds = typeof value.seconds === 'number' ? value.seconds : value._seconds;
  const nanos = typeof value.nanoseconds === 'number' ? value.nanoseconds : value._nanoseconds;

  if (typeof seconds === 'number' || typeof nanos === 'number') {
    const millis = (seconds || 0) * 1000 + Math.floor((nanos || 0) / 1_000_000);
    const date = new Date(millis);
    return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
  }

  return '';
};

const toIsoString = (value) => {
  if (!value) {
    return null;
  }

  if (typeof value === 'string') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed.toISOString();
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.toISOString();
  }

  if (typeof value.toDate === 'function') {
    const parsed = value.toDate();
    return parsed instanceof Date && !Number.isNaN(parsed.getTime()) ? parsed.toISOString() : null;
  }

  const seconds = typeof value.seconds === 'number' ? value.seconds : value._seconds;
  const nanos = typeof value.nanoseconds === 'number' ? value.nanoseconds : value._nanoseconds;

  if (typeof seconds === 'number' || typeof nanos === 'number') {
    const millis = (seconds || 0) * 1000 + Math.floor((nanos || 0) / 1_000_000);
    const date = new Date(millis);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
  }

  return null;
};

const ensureCategories = (source = {}, keys = []) => {
  return keys.reduce((acc, key) => {
    acc[key] = toNumber(source?.[key], 0);
    return acc;
  }, {});
};

const sanitizeString = (value) => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length ? trimmed : undefined;
};

const sanitizeDateInput = (value) => {
  const dateOnly = toDateOnly(value);
  return dateOnly || undefined;
};

const sanitizeObject = (obj = {}) => {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== undefined)
  );
};

const buildPaymentPayload = (formData = {}) => {
  const payload = {
    recipientName: sanitizeString(formData.recipientName),
    recipientType: sanitizeString(formData.recipientType),
    recipientId: sanitizeString(formData.recipientId),
    paymentType: sanitizeString(formData.paymentType),
    paymentCalculationType: sanitizeString(formData.paymentCalculationType),
    payPeriodStart: sanitizeDateInput(formData.payPeriodStart),
    payPeriodEnd: sanitizeDateInput(formData.payPeriodEnd),
    paymentDate: sanitizeDateInput(formData.paymentDate || formData.payDate),
    paymentMethod: sanitizeString(formData.paymentMethod),
    status: sanitizeString(formData.status),
    loadCount: Number.isFinite(Number(formData.loadCount)) ? Number(formData.loadCount) : undefined,
    totalMiles: Number.isFinite(Number(formData.totalMiles)) ? Number(formData.totalMiles) : undefined,
    grossRevenue: Number.isFinite(Number(formData.grossRevenue)) ? Number(formData.grossRevenue) : undefined,
    netRevenue: Number.isFinite(Number(formData.netRevenue)) ? Number(formData.netRevenue) : undefined,
    loadAmount: Number.isFinite(Number(formData.loadAmount)) ? Number(formData.loadAmount) : undefined,
    percentageRate: Number.isFinite(Number(formData.percentageRate)) ? Number(formData.percentageRate) : undefined,
    salaryAmount: Number.isFinite(Number(formData.salaryAmount)) ? Number(formData.salaryAmount) : undefined,
    mileageRate: Number.isFinite(Number(formData.mileageRate)) ? Number(formData.mileageRate) : undefined,
    commissionRate: Number.isFinite(Number(formData.commissionRate)) ? Number(formData.commissionRate) : undefined,
    loadIds: Array.isArray(formData.loadIds) ? formData.loadIds : undefined,
    deductions: ensureCategories(formData.deductions, DEDUCTION_KEYS),
    bonuses: ensureCategories(formData.bonuses, BONUS_KEYS),
    notes: sanitizeString(formData.notes),
    internalNotes: sanitizeString(formData.internalNotes),
    attachments: Array.isArray(formData.attachments) ? formData.attachments : undefined,
    grossRevenueBreakdown: formData.grossRevenueBreakdown,
    bankAccount: formData.bankAccount || undefined
  };

  if (formData.mileageRate !== undefined && payload.mileageRate === undefined) {
    const numeric = Number(formData.mileageRate);
    payload.mileageRate = Number.isFinite(numeric) ? numeric : undefined;
  }

  return sanitizeObject(payload);
};

const normalizePayment = (raw = {}) => {
  if (!raw) {
    return null;
  }

  const paymentNumber = raw.paymentNumber || raw.paymentId || raw.id || '';
  const paymentDate = toDateOnly(raw.paymentDate || raw.payDate);

  return {
    id: raw.id || raw.paymentId || paymentNumber,
    paymentId: raw.paymentId || raw.id || paymentNumber,
    paymentNumber,
    companyId: raw.companyId || '',
    recipientId: raw.recipientId || '',
    recipientName: raw.recipientName || '',
    recipientType: raw.recipientType || 'driver',
    paymentType: raw.paymentType || 'weekly_settlement',
    paymentCalculationType: raw.paymentCalculationType || null,
    payPeriodStart: toDateOnly(raw.payPeriodStart),
    payPeriodEnd: toDateOnly(raw.payPeriodEnd),
    paymentDate,
    paymentMethod: raw.paymentMethod || 'direct_deposit',
    status: raw.status || 'pending',
    loadCount: toInteger(raw.loadCount),
    totalMiles: toNumber(raw.totalMiles),
    grossRevenue: toNumber(raw.grossRevenue),
    netRevenue: toNumber(raw.netRevenue),
    loadAmount: toNumber(raw.loadAmount),
    percentageRate: toNumber(raw.percentageRate),
    salaryAmount: toNumber(raw.salaryAmount),
    mileageRate: toNumber(raw.mileageRate),
    commissionRate: toNumber(raw.commissionRate),
    commission: toNumber(raw.commission),
    basePay: toNumber(raw.basePay),
    calculatedBasePay: toNumber(raw.basePay ?? raw.calculatedBasePay),
    totalDeductions: toNumber(raw.totalDeductions),
    totalBonuses: toNumber(raw.totalBonuses),
    netPayment: toNumber(raw.netPayment ?? raw.amount),
    deductions: ensureCategories(raw.deductions, DEDUCTION_KEYS),
    bonuses: ensureCategories(raw.bonuses, BONUS_KEYS),
    loadIds: Array.isArray(raw.loadIds) ? raw.loadIds : [],
    attachments: Array.isArray(raw.attachments) ? raw.attachments : [],
    notes: raw.notes || '',
    internalNotes: raw.internalNotes || '',
    approvedBy: raw.approvedBy || null,
    bankAccount: raw.bankAccount || null,
    paystubUrl: raw.paystubUrl || '',
    createdBy: raw.createdBy || null,
    createdAt: toIsoString(raw.createdAt),
    updatedAt: toIsoString(raw.updatedAt),
    paidAt: toIsoString(raw.paidAt),
    voidedAt: toIsoString(raw.voidedAt)
  };
};

const normalizePagination = (raw = {}, { page = 1, limit = 25 } = {}) => {
  return {
    page: raw.page ?? page,
    limit: raw.limit ?? limit,
    totalCount: raw.totalCount ?? raw.total ?? 0,
    totalPages: raw.totalPages ?? 1,
    hasNext: Boolean(raw.hasNext),
    hasPrev: Boolean(raw.hasPrev)
  };
};

class PaymentService {
  constructor() {
    this.basePath = '/payments';
  }

  buildListParams(params = {}) {
    const cleaned = {
      recipientId: sanitizeString(params.recipientId),
      recipientType: sanitizeString(params.recipientType),
      status: sanitizeString(params.status),
      paymentType: sanitizeString(params.paymentType),
      paymentCalculationType: sanitizeString(params.paymentCalculationType),
      paymentMethod: sanitizeString(params.paymentMethod),
      payPeriodStart: sanitizeDateInput(params.payPeriodStart),
      payPeriodEnd: sanitizeDateInput(params.payPeriodEnd),
      paymentDateStart: sanitizeDateInput(params.paymentDateStart),
      paymentDateEnd: sanitizeDateInput(params.paymentDateEnd),
      sortBy: sanitizeString(params.sortBy),
      sortOrder: sanitizeString(params.sortOrder),
      page: params.page,
      limit: params.limit
    };

    return sanitizeObject(cleaned);
  }

  async listPayments(params = {}) {
    try {
      const query = this.buildListParams(params);
      const response = await api.get(this.basePath, { params: query });
      const payments = Array.isArray(response?.data)
        ? response.data.map(normalizePayment).filter(Boolean)
        : [];

      return {
        payments,
        pagination: normalizePagination(response?.pagination, query)
      };
    } catch (error) {
      throw error instanceof ApiError ? error : ApiError.fromResponse(error);
    }
  }

  async getPayment(paymentId) {
    if (!paymentId) {
      throw new Error('Payment ID is required');
    }

    try {
      const response = await api.get(`${this.basePath}/${paymentId}`);
      const payment = response?.data || response;
      return normalizePayment(payment);
    } catch (error) {
      throw error instanceof ApiError ? error : ApiError.fromResponse(error);
    }
  }

  async createPayment(formData) {
    try {
      const payload = buildPaymentPayload(formData);
      const response = await api.post(this.basePath, payload);
      const payment = response?.data || response;
      return normalizePayment(payment);
    } catch (error) {
      throw error instanceof ApiError ? error : ApiError.fromResponse(error);
    }
  }

  async updatePayment(paymentId, formData) {
    if (!paymentId) {
      throw new Error('Payment ID is required to update a payment');
    }

    try {
      const payload = buildPaymentPayload(formData);
      const response = await api.put(`${this.basePath}/${paymentId}`, payload);
      const payment = response?.data || response;
      return normalizePayment(payment);
    } catch (error) {
      throw error instanceof ApiError ? error : ApiError.fromResponse(error);
    }
  }

  async updatePaymentStatus(paymentId, status) {
    if (!paymentId) {
      throw new Error('Payment ID is required to update status');
    }

    if (!status) {
      throw new Error('Status is required');
    }

    try {
      const response = await api.patch(`${this.basePath}/${paymentId}/status`, { status });
      const payment = response?.data || response;
      return normalizePayment(payment);
    } catch (error) {
      throw error instanceof ApiError ? error : ApiError.fromResponse(error);
    }
  }

  async deletePayment(paymentId) {
    if (!paymentId) {
      throw new Error('Payment ID is required to delete a payment');
    }

    try {
      const response = await api.delete(`${this.basePath}/${paymentId}`);
      return response;
    } catch (error) {
      throw error instanceof ApiError ? error : ApiError.fromResponse(error);
    }
  }

  async getPendingPayments() {
    try {
      const response = await api.get(`${this.basePath}/pending`);
      const payload = response?.data || response;
      const payments = Array.isArray(payload?.payments)
        ? payload.payments.map(normalizePayment).filter(Boolean)
        : [];

      return {
        payments,
        totalPending: toNumber(payload?.totalPending),
        count: payload?.count ?? payments.length
      };
    } catch (error) {
      throw error instanceof ApiError ? error : ApiError.fromResponse(error);
    }
  }

  async getPaymentStatistics(filters = {}) {
    try {
      const params = sanitizeObject({
        startDate: sanitizeDateInput(filters.startDate),
        endDate: sanitizeDateInput(filters.endDate)
      });
      const response = await api.get(`${this.basePath}/statistics`, { params });
      return response?.data || response;
    } catch (error) {
      throw error instanceof ApiError ? error : ApiError.fromResponse(error);
    }
  }
}

const paymentService = new PaymentService();

export default paymentService;
